import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchUser, updateUser,
  uploadProfilePhoto, removeProfilePhoto,
  fetchAvailabilitySchedule,
  fetchAdHocAvailabilitySchedules, deleteAdHocAvailabilitySchedule,
  fetchDaysOffs, createDaysOff, deleteDaysOff,
} from "api";
import { PageHeader, FormField, Spinner, Modal } from "components/common";
import { AvailabilityDisplay, AvailabilityModal, AdHocAvailabilityModal } from "components/availability";
import { useAuth } from "hooks/useAuth";
import type { AdHocAvailabilitySchedule, DaysOff, DaysOffStatus } from "types";

const STATUS_STYLES: Record<DaysOffStatus, string> = {
  requested: "bg-amber-100 text-amber-700",
  approved:  "bg-green-100 text-green-700",
  rejected:  "bg-red-100 text-red-600",
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month:   "short",
    day:     "numeric",
    year:    "numeric",
    hour:    "numeric",
    minute:  "2-digit",
  });
}

export default function AccountPage() {
  const { userId, isCourier } = useAuth();
  const queryClient = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [showAvailModal, setShowAvailModal] = useState(false);

  const { data: user, isLoading } = useQuery({
    queryKey: ["contactUser", userId],
    queryFn:  () => fetchUser(userId!),
    enabled:  !!userId,
  });

  const { data: availSchedule } = useQuery({
    queryKey: ["availabilitySchedule", userId],
    queryFn:  () => fetchAvailabilitySchedule(userId!),
    enabled:  !!userId && isCourier,
  });

  const { data: adHocSchedules = [] } = useQuery({
    queryKey: ["adHocAvailabilitySchedules", userId],
    queryFn:  () => fetchAdHocAvailabilitySchedules(userId!),
    enabled:  !!userId && isCourier,
  });

  const { data: daysOffs = [] } = useQuery({
    queryKey: ["daysOffs", userId],
    queryFn:  () => fetchDaysOffs(userId!),
    enabled:  !!userId && isCourier,
  });

  const [showDaysOffModal,    setShowDaysOffModal]    = useState(false);
  const [showAdHocModal,      setShowAdHocModal]      = useState(false);
  const [editingAdHoc,        setEditingAdHoc]        = useState<AdHocAvailabilitySchedule | null>(null);

  // ── Profile form ────────────────────────────────────────────────────────────
  const [name1, setName1]   = useState("");
  const [name2, setName2]   = useState("");
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");

  useEffect(() => {
    if (user) {
      setName1(user.name_1 ?? "");
      setName2(user.name_2 ?? "");
      setEmail(user.email ?? "");
      setPhone(user.phone ?? "");
    }
  }, [user]);

  const { mutate: saveProfile, isPending: saving, isSuccess: saved, error: saveError } = useMutation({
    mutationFn: () =>
      updateUser(userId!, { name_1: name1, name_2: name2 || undefined, email: email || undefined, phone: phone || undefined }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["contactUser", userId], updated);
    },
  });

  // ── Profile photo ───────────────────────────────────────────────────────────
  const { mutate: uploadPhoto, isPending: uploadingPhoto } = useMutation({
    mutationFn: (file: File) => uploadProfilePhoto(userId!, file),
    onSuccess: (updated) => {
      queryClient.setQueryData(["contactUser", userId], updated);
      if (photoInputRef.current) photoInputRef.current.value = "";
    },
  });

  const { mutate: removePhoto, isPending: removingPhoto } = useMutation({
    mutationFn: () => removeProfilePhoto(userId!),
    onSuccess: (updated) => {
      queryClient.setQueryData(["contactUser", userId], updated);
    },
  });

  if (isLoading || !user) return <Spinner className="mt-20" />;

  const initials = [user.name_1, user.name_2]
    .filter(Boolean)
    .map((n) => n![0].toUpperCase())
    .join("");

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-lg">
      <PageHeader title="Account" subtitle="Manage your profile" />

      {/* ── Profile photo ──────────────────────────────────────────────────── */}
      <div className="card p-6 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-4">Profile photo</h2>
        <div className="flex items-center gap-5">
          {/* Avatar */}
          <div className="relative shrink-0">
            {user.profile_photo_url ? (
              <img
                src={user.profile_photo_url}
                alt="Profile"
                className="h-20 w-20 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-xl font-semibold border border-brand-200">
                {initials}
              </div>
            )}
            {(uploadingPhoto || removingPhoto) && (
              <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
                <Spinner className="h-5" />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadPhoto(file);
              }}
            />
            <button
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto || removingPhoto}
              className="btn-secondary text-xs py-1.5 px-3 block w-full text-center"
            >
              {user.profile_photo_url ? "Change photo" : "Upload photo"}
            </button>
            {user.profile_photo_url && (
              <button
                onClick={() => removePhoto()}
                disabled={uploadingPhoto || removingPhoto}
                className="text-xs text-red-500 hover:text-red-700 transition-colors w-full text-center"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Profile details ────────────────────────────────────────────────── */}
      <div className="card p-6 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-4">Profile details</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); saveProfile(); }}
          className="space-y-4"
        >
          <FormField label="Name">
            <input
              className="input"
              value={name1}
              onChange={(e) => setName1(e.target.value)}
              required
            />
          </FormField>

          <FormField label="Name 2">
            <input
              className="input"
              value={name2}
              onChange={(e) => setName2(e.target.value)}
              placeholder="Optional"
            />
          </FormField>

          <FormField label="Email">
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Optional"
            />
          </FormField>

          <FormField label="Phone">
            <input
              className="input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Optional"
            />
          </FormField>

          {saveError && (
            <p className="text-xs text-red-500">
              {(saveError as { response?: { data?: { errors?: string[] } } })
                ?.response?.data?.errors?.join(", ") ?? "Something went wrong."}
            </p>
          )}
          {saved && <p className="text-xs text-green-600">Profile saved.</p>}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={saving || !name1.trim()}
          >
            {saving ? <Spinner className="h-4" /> : "Save changes"}
          </button>
        </form>
      </div>

      {/* ── Availability schedule (couriers only) ──────────────────────────── */}
      {isCourier && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Availability schedule</h2>
            <button
              className="btn-secondary text-xs py-1"
              onClick={() => setShowAvailModal(true)}
            >
              {availSchedule ? "Edit schedule" : "Add schedule"}
            </button>
          </div>

          {availSchedule ? (
            <AvailabilityDisplay availability={availSchedule.availability} />
          ) : (
            <p className="text-sm text-gray-400">No availability schedule set.</p>
          )}
        </div>
      )}

      {/* ── Ad-hoc availability schedules (couriers only) ─────────────────── */}
      {isCourier && (
        <div className="card p-6 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Ad-hoc weekly schedules</h2>
            <button
              className="btn-secondary text-xs py-1"
              onClick={() => { setEditingAdHoc(null); setShowAdHocModal(true); }}
            >
              Add schedule
            </button>
          </div>

          {adHocSchedules.length === 0 ? (
            <p className="text-sm text-gray-400">No ad-hoc schedules. Add one to override your regular schedule for a specific week.</p>
          ) : (
            <div className="space-y-2">
              {adHocSchedules.map((s) => (
                <AdHocScheduleEntry
                  key={s.id}
                  schedule={s}
                  onEdit={() => { setEditingAdHoc(s); setShowAdHocModal(true); }}
                  onDelete={() => queryClient.invalidateQueries({ queryKey: ["adHocAvailabilitySchedules", userId] })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Days off (couriers only) ───────────────────────────────────────── */}
      {isCourier && (
        <div className="card p-6 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Days off</h2>
            <button
              className="btn-secondary text-xs py-1"
              onClick={() => setShowDaysOffModal(true)}
            >
              Request time away
            </button>
          </div>

          {daysOffs.length === 0 ? (
            <p className="text-sm text-gray-400">No time away requests.</p>
          ) : (
            <div className="space-y-2">
              {daysOffs.map((d) => (
                <DaysOffEntry
                  key={d.id}
                  daysOff={d}
                  onDelete={() => queryClient.invalidateQueries({ queryKey: ["daysOffs", userId] })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showAvailModal && (
        <AvailabilityModal
          userId={userId!}
          existing={availSchedule ?? null}
          onClose={() => setShowAvailModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["availabilitySchedule", userId] });
            setShowAvailModal(false);
          }}
        />
      )}

      {showAdHocModal && (
        <AdHocAvailabilityModal
          userId={userId!}
          existing={editingAdHoc}
          onClose={() => { setShowAdHocModal(false); setEditingAdHoc(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["adHocAvailabilitySchedules", userId] });
            setShowAdHocModal(false);
            setEditingAdHoc(null);
          }}
        />
      )}

      {showDaysOffModal && (
        <RequestDaysOffModal
          userId={userId!}
          onClose={() => setShowDaysOffModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["daysOffs", userId] });
            setShowDaysOffModal(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Days off entry ───────────────────────────────────────────────────────────

function DaysOffEntry({ daysOff, onDelete }: { daysOff: DaysOff; onDelete: () => void }) {
  const { mutate: withdraw, isPending } = useMutation({
    mutationFn: () => deleteDaysOff(daysOff.id),
    onSuccess:  onDelete,
  });

  return (
    <div className="flex items-start justify-between gap-4 bg-gray-50 rounded-lg px-4 py-3">
      <div className="space-y-0.5 text-sm">
        <div className="text-gray-800 font-medium">
          {formatDateTime(daysOff.start_at)} – {formatDateTime(daysOff.end_at)}
        </div>
        <span className={`badge text-xs ${STATUS_STYLES[daysOff.status]}`}>
          {daysOff.status.charAt(0).toUpperCase() + daysOff.status.slice(1)}
        </span>
      </div>
      {daysOff.status === "requested" && (
        <button
          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50 shrink-0"
          disabled={isPending}
          onClick={() => withdraw()}
        >
          {isPending ? "…" : "Withdraw"}
        </button>
      )}
    </div>
  );
}

// ─── Ad-hoc schedule entry ────────────────────────────────────────────────────

function formatWeekRange(monday: string): string {
  const d = new Date(`${monday}T00:00:00`);
  const end = new Date(d);
  end.setDate(d.getDate() + 6);
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} – ${end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;
}

function AdHocScheduleEntry({
  schedule,
  onEdit,
  onDelete,
}: {
  schedule: AdHocAvailabilitySchedule;
  onEdit:   () => void;
  onDelete: () => void;
}) {
  const activeDays = Object.keys(schedule.availability).length;

  const { mutate: remove, isPending } = useMutation({
    mutationFn: () => deleteAdHocAvailabilitySchedule(schedule.id),
    onSuccess:  onDelete,
  });

  return (
    <div className="flex items-center justify-between gap-4 bg-gray-50 rounded-lg px-4 py-3">
      <div className="space-y-0.5 text-sm min-w-0">
        <div className="text-gray-800 font-medium">{formatWeekRange(schedule.week_start_date)}</div>
        <div className="text-xs text-gray-500">
          {activeDays === 0 ? "No days set" : `${activeDays} day${activeDays !== 1 ? "s" : ""} configured`}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors"
          onClick={onEdit}
        >
          Edit
        </button>
        <button
          className="text-xs px-2 py-1 rounded-lg border border-red-100 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
          disabled={isPending}
          onClick={() => remove()}
        >
          {isPending ? "…" : "Delete"}
        </button>
      </div>
    </div>
  );
}

// ─── Request days off modal ───────────────────────────────────────────────────

function RequestDaysOffModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId:    number;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const [startAt, setStartAt] = useState("");
  const [endAt,   setEndAt]   = useState("");

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      createDaysOff({
        contact_user_id: userId,
        start_at:        new Date(startAt).toISOString(),
        end_at:          new Date(endAt).toISOString(),
      }),
    onSuccess,
  });

  const endBeforeStart = startAt && endAt && new Date(endAt) <= new Date(startAt);
  const isValid = startAt && endAt && !endBeforeStart;

  return (
    <Modal title="Request time away" onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Start">
          <input
            type="datetime-local"
            className="input"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </FormField>
        <FormField label="End">
          <input
            type="datetime-local"
            className="input"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
          />
        </FormField>
        {endBeforeStart && (
          <p className="text-xs text-red-500">End must be after start.</p>
        )}
        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Submit request"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
